<template>
  <div class="bg-white dark:bg-dark-box-background h-[100%] rounded-t-lg" @dbclick="$emit('dbclick')">
    <UpdateRecord
      v-model:is-open="update_record_modal_is_open"
      :component-reference="componentReference"
      :object-type="forecast_object_type"
      :view-type="VIEW_TYPES.FORM_UPDATE"
      :page-reference="PAGE_REFERENCES.DASHBOARD"
      :selected-record="selected_record"
      @updateMap="$emitter.emit('refreshTable')" />
    <div class="flex flex-col w-full">
      <div class="py-2 mb-1 ml-2 text-xl font-medium text-light-body-text dark:text-dark-body-text">
        {{ label ? label : $t("Outstanding Cases") }}
      </div>
      <div
        class="bg-white flex items-center justify-center gap-1 w-full h-[40px] max-h-[40px] border-b-[1px] rounded-t-lg pb-6 py-6 border-light-divider-border dark:border-dark-divider-border dark:bg-dark-box-background min-w-0">
        <button
          class="w-fit primary-btn inline-flex whitespace-nowrap items-center justify-center h-[38px] max-h-[38px] px-5 py-[7px] font-normal rounded-md shadow-sm"
          @click="handleClickFilter">
          <ListFilter color="white" class="w-5 h-5 mr-3 group-hover:text-gray-500" aria-hidden="true" />
          <span v-if="itemLayoutWidth > 8" class="pl-2">{{ is_filter_open ? $t("Clear Filter") : $t("Add Filter") }}</span>
        </button>
        <SelectGroupDropDownMenu
          v-if="!view_by"
          v-model:current-item="selected_group"
          :items="groups"
          :object-type="forecast_object_type"
          label-attr="name"
          class="-mt-2 min-w-[200px] h-[38px] relative"
          :is-box-background="true"
          input-classes="sm:text-sm appearance-none border !border-gray-300 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
        <div v-if="!forecast_name" class="flex flex-row items-center ml-2">
          <div v-for="bread in forecasting_breads" :key="bread.id" class="flex items-center">
            <ChevronRight class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
            <component :is="bread.component" />
          </div>
        </div>
        <div class="relative w-full">
          <div class="absolute inset-y-0 flex items-center pointer-events-none left-2">
            <Search class="z-10 w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>
          <TextInput
            :disabled="editing_disabled"
            :icon="true"
            :value="search_term"
            input-classes="appearance-none block py-2 rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            class=""
            name="account_name"
            type="text"
            :debounce="800"
            :validate-on-input="true"
            label=""
            :show-label="false"
            :placeholder="$t('Search...')"
            @update:modelValue="
              (e) => {
                search_term = e
              }
            " />
        </div>

        <QuarterDatePicker
          v-show="itemLayoutWidth > 5"
          v-model:current-range="selected_range"
          class="min-w-[250px] bg-white dark:bg-dark-box-background rounded-lg" />

        <div
          v-if="!editing_disabled"
          class="flex items-center px-2 py-1 h-[38px] max-h-[38px] w-fit relative rounded-lg primary-btn dark:bg-dark-brand-blue dark:hover:bg-dark-brand-blue/80 text-dark-body-text transition ease-in-out duration-side-menu-transition-duration cursor-pointer"
          @click="add_company_modal_is_open = true">
          <span>
            <X class="min-w-[20px] h-7 w-5 transform rotate-45" />
          </span>
          <div v-show="itemLayoutWidth > 8" class="pl-2 font-semibold capitalize flex-nowrap min-w-fit w-fit whitespace-nowrap">
            {{ $t("Add") }} {{ forecast_object_type }}
          </div>
        </div>
        <div class="-ml-2 has-tooltip">
          <RecordActionsDropDown
            v-if="!editing_disabled"
            :box-background="true"
            :disabled="editing_disabled"
            :component-reference="componentReference"
            :icon-button="true"
            :object-type="forecast_obj?.type"
            :page-reference="PAGE_REFERENCES.FORECASTING"
            :selected-records="Array.from(selected_records)"
            :selected-full-records="Array.from(selected_full_records)" />
          <span v-if="editing_disabled"
                class="flex-wrap p-2 -ml-40 bg-gray-300 rounded tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
          </span>
        </div>
        <div class="mr-1">
          <button
            :disabled="editing_disabled"
            class="inline-flex px-2 py-2 font-medium border border-gray-200 rounded-md has-tooltip hover:bg-gray-100 sm:text-sm focus:outline-none"
            @click="$emitter.emit(ALL_EMITTERS.UPDATE_FORECAST_TABLE_METRICS, componentReference)">
            <Grid2x2Plus class="w-5 h-5 text-gray-500" aria-hidden="true" />
            <span v-if="editing_disabled"
                  class="flex-wrap p-2 bg-gray-300 rounded -ml-52 mt-7 text- tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="overflow-y-auto overflow-x-auto w-full max-w-full h-[calc(100%-155px)] mb-5">
      <table
        id="forecasting_table"
        class="min-w-full transition ease-in-out divide-y divide-light-divider-border dark:divide-dark-divider-border duration-side-menu-transition-duration">
        <thead
          v-if="!is_filter_open"
          class="w-full transition ease-in-out bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration">
          <draggable
            v-model="headers"
            :component-data="{
              type: 'transition-group',
              name: !drag ? 'flip-list' : null
            }"
            v-bind="{
              animation: 200,
              group: 'forecast header',
              ghostClass: 'ghost',
              chosenClass: 'chosen'
            }"
            handle=".drag-handle"
            filter=".ignore-drag"
            item-key="id"
            class="text-left rounded-lg shadow-sm text-common-grey"
            tag="tr"
            @start="drag = true"
            @end="
              () => {
                drag = false
                updateForecastHeaders()
              }
            ">
            <template #item="{ element }">
              <th v-if="element.id == 'checkbox'" :class="element.class">
                <div class="flex items-center justify-center">
                  <input
                    v-model="select_all_records"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                </div>
              </th>
              <th v-else :class="element.class">
                <div class="flex items-center">
                  <span>{{ element.label }}</span>
                  <component :is="element.icon" v-if="element.icon" class="ml-2 w-3.5 h-3.5" />
                </div>
              </th>
            </template>
            <template #footer>
              <th class="" />
            </template>
          </draggable>
        </thead>
        <thead
          v-else
          class="w-full transition ease-in-out bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration">
          <tr class="text-left rounded-lg shadow-sm text-common-grey">
            <template v-for="header in headers" :key="header.id">
              <th v-if="header.id == 'checkbox'">
                <div class="flex items-center justify-center">
                  <input
                    v-model="select_all_records"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                </div>
              </th>
              <th
                v-else-if="header.id == 'account'"
                class="px-3 py-3 whitespace-nowrap font-light bg-white dark:bg-dark-box-background freeze-column min-w-[500px] w-[500px]">
                <div class="relative w-full">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search class="z-10 w-5 h-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <TextInput
                    :disabled="editing_disabled"
                    :in-table-filter="true"
                    :value="search_term"
                    :icon="name_pencil_icon"
                    input-classes="w-full mt-1 appearance-none block font-medium py-2 border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                    name="account_name"
                    type="text"
                    class="-mt-1"
                    :debounce="800"
                    :validate-on-input="true"
                    label=""
                    :show-label="false"
                    :placeholder="$t('Account')"
                    @update:modelValue="
                      (e) => {
                        search_term = e
                      }
                    " />
                </div>
              </th>
              <th v-else-if="header.id == 'owner'" class="px-3 py-3 font-light whitespace-nowrap">
                <ComboBoxInput
                  v-model="filter_selected_owners"
                  :disabled="editing_disabled"
                  name="owners"
                  :items="$store.state.data?.users"
                  label-attr="full_name"
                  :is-multiple="true"
                  :allow-null="true"
                  :return-value="true"
                  :icon="user_menu_icon"
                  value-attr="id"
                  :label="$t('Owners')"
                  classes="w-full dark:text-dark-body-text min-w-[150px] w-[150px]"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text="Selected owners (%COUNT)"
                  :placeholder="$t('Owners')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'co_owners'" class="px-3 py-3 font-light whitespace-nowrap">
                <ComboBoxInput
                  v-model="filter_selected_co_owners"
                  :disabled="editing_disabled"
                  name="co_owners"
                  :items="$store.state.data?.users"
                  label-attr="full_name"
                  value-attr="id"
                  :return-value="true"
                  :allow-null="true"
                  :is-multiple="true"
                  :icon="users_menu_icon"
                  :label="$t('Co owners')"
                  classes="w-full dark:text-dark-body-text min-w-[150px] w-[150px]"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text="Selected co-owners (%COUNT)"
                  :placeholder="$t('Co-Owner')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'tags'" class="px-3 py-3 font-light whitespace-nowrap">
                <ComboBoxInput
                  v-model="filter_selected_tags"
                  name="owners"
                  :items="$store.state.data.tags"
                  label-attr="name"
                  :is-multiple="true"
                  :allow-null="true"
                  :return-value="true"
                  :icon="user_menu_icon"
                  value-attr="id"
                  :label="$t('Tags')"
                  classes="w-full dark:text-dark-body-text min-w-[150px] w-[150px]"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text=""
                  :placeholder="$t('Tags')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'forecast_type'" class="px-3 py-3 font-light whitespace-nowrap">
                <MultiSelectInput
                  v-model:current-items="forecast_type"
                  :disabled="editing_disabled"
                  name="forecast_type"
                  :items="forecast_types"
                  label-attr="name"
                  value-attr="value"
                  :allow-null="true"
                  :icon="types_icon"
                  :label="$t('Type')"
                  classes=""
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  :placeholder="$t('Type')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'flag'" class="px-3 py-3 font-light whitespace-nowrap">
                <MultiSelectInput
                  v-model:current-items="flag"
                  :disabled="editing_disabled"
                  name="forecast_type"
                  :items="flags"
                  label-attr="name"
                  value-attr="value"
                  :allow-null="true"
                  :icon="types_icon"
                  :label="$t('Flag')"
                  classes=""
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  :placeholder="$t('Flag')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'forecasted_date'" class="px-3 py-3 font-light whitespace-nowrap">
                <DatePicker
                  v-model="forecast_date_range"
                  :input-debounce="500"
                  mode="range"
                  is-range
                  class="w-full bg-white dark:bg-dark-screen-background"
                  :attributes="[
                    {
                      key: 'today',
                      dot: true,
                      dates: new Date()
                    }
                  ]"
                  :popover="{ visibility: 'click' }"
                  range>
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative flex-grow">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-400 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <button
                        v-if="forecast_date_range && forecast_date_range.start"
                        class="absolute right-0 flex items-center justify-center w-6 h-6 mx-2 rounded-full top-2 group hover:bg-gray-300 rounderd-full"
                        type="button"
                        @click="forecast_date_range = null">
                        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
                      </button>

                      <input
                        :value="forecast_date_range && forecast_date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                        placeholder="Due date"
                        :disabled="true"
                        class="w-full px-10 py-2 pr-2 text-sm font-medium placeholder-gray-400 bg-white border border-gray-300 rounded-md outline-none dark:bg-dark-box-background dark:border-dark-divider-border text-common-grey focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                        v-on="inputEvents.start || inputEvents.end"
                        @input="
                          $utils.debounce(() => {
                            forecast_date_range = $event.target.value
                          }, 800)
                        ">
                    </div>
                  </template>
                </DatePicker>
              </th>
              <th v-else-if="header.id == 'base_value'" class="px-3 py-3 font-light whitespace-nowrap">
                <div class="relative font-medium text-gray-500 text-md">
                  <div class="inline-flex w-full rounded-md shadow-sm cursor-pointer" @click="is_input_base_value = !is_input_base_value">
                    <span
                      v-if="base_value_gte && base_value_lte && !is_input_base_value"
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      {{ base_value_gte }} - {{ base_value_lte }}
                    </span>
                    <div
                      v-else
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      <Layers class="w-5 h-5 mr-2" /><span> {{ $t("Base Value") }} </span>
                    </div>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </div>

                  <div
                    v-if="is_input_base_value"
                    class="origin-bottom-left absolute z-50 text-center focus:outline-none bg-light-divider dark:bg-dark-box-background p-3 border dark:border-dark-divider-border rounded-lg mt-1 w-[225px] space-y-2">
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      label="Min"
                      name="min"
                      :value="base_value_gte"
                      @input="
                        ($event) => {
                          base_value_gte = $event.target.value
                        }
                      " />
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      label="Max"
                      name="max"
                      :value="base_value_lte"
                      @input="
                        ($event) => {
                          base_value_lte = $event.target.value
                        }
                      " />
                    <div class="flex my-1 gap-x-2">
                      <button
                        type="button"
                        class="cancel-btn -ml-0.5 w-1/2"
                        @click="
                          () => {
                            cancelFilterBaseValue()
                          }
                        ">
                        {{ $t("Cancel") }}
                      </button>
                      <button
                        type="button"
                        class="w-1/2 primary-btn"
                        @click="
                          () => {
                            filterBaseValue()
                            is_input_base_value = false
                          }
                        ">
                        {{ $t("Filter") }}
                      </button>
                    </div>
                  </div>
                </div>
              </th>
              <th v-else-if="header.id == 'original_base_value'" class="px-3 py-3 font-light whitespace-nowrap">
                <div class="relative font-medium text-gray-400">
                  <div class="inline-flex w-full rounded-md shadow-sm cursor-pointer">
                    {{ $t("Original Base") }}
                  </div>
                </div>
              </th>
              <th v-else-if="header.id == 'forecast_status'" class="px-3 py-3 font-light whitespace-nowrap">
                <MultiSelectInput
                  v-model:current-items="forecast_status"
                  :disabled="editing_disabled"
                  name="forecast_status"
                  :items="forecast_statuses"
                  label-attr="name"
                  value-attr="value"
                  :allow-null="true"
                  :icon="status_icon"
                  :label="$t('Status')"
                  classes=""
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  :placeholder="$t('status')"
                  :show-label="false" />
              </th>
              <th v-else-if="header.id == 'forecasted_value'" class="px-3 py-3 font-light whitespace-nowrap">
                <div class="relative font-medium text-gray-500 text-md">
                  <div
                    class="inline-flex w-full rounded-md shadow-sm cursor-pointer"
                    @click="is_input_forecasted_value = !is_input_forecasted_value">
                    <span
                      v-if="forecasted_value_gte && forecasted_value_lte && !is_input_forecasted_value"
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      {{ forecasted_value_gte }} - {{ forecasted_value_lte }}
                    </span>
                    <div
                      v-else
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      <TrendingUp class="w-5 h-5 mr-2" /><span> {{ $t("Forecasted Value") }} </span>
                    </div>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </div>

                  <div
                    v-if="is_input_forecasted_value"
                    class="origin-bottom-left absolute z-50 text-center focus:outline-none bg-light-divider dark:bg-dark-box-background p-3 border dark:border-dark-divider-border rounded-lg mt-1 w-[225px] space-y-2">
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Min')"
                      name="min"
                      :value="forecasted_value_gte"
                      @input="
                        ($event) => {
                          forecasted_value_gte = $event.target.value
                        }
                      " />
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Max')"
                      name="max"
                      :value="forecasted_value_lte"
                      @input="
                        ($event) => {
                          forecasted_value_lte = $event.target.value
                        }
                      " />
                    <div class="flex my-1 gap-x-2">
                      <button
                        type="button"
                        class="cancel-btn -ml-0.5 w-1/2"
                        @click="
                          () => {
                            cancelFilterForecastedValue()
                          }
                        ">
                        {{ $t("Cancel") }}
                      </button>
                      <button
                        type="button"
                        class="w-1/2 primary-btn"
                        @click="
                          () => {
                            filterForecastedValue()
                          }
                        ">
                        {{ $t("Filter") }}
                      </button>
                    </div>
                  </div>
                </div>
              </th>
              <th v-else-if="header.id == 'forecasted_value_max'" class="px-3 py-3 font-light whitespace-nowrap">
                <div class="relative font-medium text-gray-500 text-md">
                  <div
                    class="inline-flex w-full rounded-md shadow-sm cursor-pointer"
                    @click="is_input_forecasted_value_max = !is_input_forecasted_value_max">
                    <span
                      v-if="forecasted_value_max_gte && forecasted_value_max_lte && !is_input_forecasted_value_max"
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      {{ forecasted_value_max_gte }} - {{ forecasted_value_max_lte }}
                    </span>
                    <div
                      v-else
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      <TrendingUp class="w-5 h-5 mr-2" /><span> {{ $t("Best Case") }} </span>
                    </div>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </div>

                  <div
                    v-if="is_input_forecasted_value_max"
                    class="origin-bottom-left absolute z-50 text-center focus:outline-none bg-light-divider dark:bg-dark-box-background p-3 border dark:border-dark-divider-border rounded-lg mt-1 w-[225px] space-y-2">
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Min')"
                      name="min"
                      :value="forecasted_value_max_gte"
                      @input="
                        ($event) => {
                          forecasted_value_max_gte = $event.target.value
                        }
                      " />
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Max')"
                      name="max"
                      :value="forecasted_value_max_lte"
                      @input="
                        ($event) => {
                          forecasted_value_max_lte = $event.target.value
                        }
                      " />
                    <div class="flex my-1 gap-x-2">
                      <button
                        type="button"
                        class="cancel-btn -ml-0.5 w-1/2"
                        @click="
                          () => {
                            cancelFilterForecastedValueMax()
                          }
                        ">
                        {{ $t("Cancel") }}
                      </button>
                      <button
                        type="button"
                        class="w-1/2 primary-btn"
                        @click="
                          () => {
                            filterForecastedValueMax()
                          }
                        ">
                        {{ $t("Filter") }}
                      </button>
                    </div>
                  </div>
                </div>
              </th>
              <th v-else-if="header.id == 'forecasted_value_min'" class="px-3 py-3 font-light whitespace-nowrap">
                <div class="relative font-medium text-gray-500 text-md">
                  <div
                    class="inline-flex w-full rounded-md shadow-sm cursor-pointer"
                    @click="is_input_forecasted_value_min = !is_input_forecasted_value_min">
                    <span
                      v-if="forecasted_value_min_gte && forecasted_value_min_lte && !is_input_forecasted_value_min"
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      {{ forecasted_value_min_gte }} - {{ forecasted_value_min_lte }}
                    </span>
                    <div
                      v-else
                      class="inline-flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-common-grey dark:border-gray-600 dark:bg-dark-box-background">
                      <TrendingUp class="w-5 h-5 mr-2" /><span> {{ $t("Worst Value") }} </span>
                    </div>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </div>

                  <div
                    v-if="is_input_forecasted_value_min"
                    class="origin-bottom-left absolute z-50 text-center focus:outline-none bg-light-divider dark:bg-dark-box-background p-3 border dark:border-dark-divider-border rounded-lg mt-1 w-[225px] space-y-2">
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Min')"
                      name="min"
                      :value="forecasted_value_min_gte"
                      @input="
                        ($event) => {
                          forecasted_value_min_gte = $event.target.value
                        }
                      " />
                    <TextInput
                      :disabled="editing_disabled"
                      type="text"
                      :label="$t('Max')"
                      name="max"
                      :value="forecasted_value_min_lte"
                      @input="
                        ($event) => {
                          forecasted_value_min_lte = $event.target.value
                        }
                      " />
                    <div class="flex my-1 gap-x-2">
                      <button
                        type="button"
                        class="cancel-btn -ml-0.5 w-1/2"
                        @click="
                          () => {
                            cancelFilterForecastedValueMin()
                          }
                        ">
                        {{ $t("Cancel") }}
                      </button>
                      <button
                        type="button"
                        class="w-1/2 primary-btn"
                        @click="
                          () => {
                            filterForecastedValueMin()
                          }
                        ">
                        {{ $t("Filter") }}
                      </button>
                    </div>
                  </div>
                </div>
              </th>
              <th v-else-if="header.id == 'grr'" class="px-3 py-3 whitespace-nowrap">
                <span class="font-medium text-gray-400">{{ $t("GRR") }}</span>
              </th>
              <th v-else-if="header.id == 'nrr'" class="px-3 py-3 whitespace-nowrap">
                <span class="font-medium text-gray-400">{{ $t("NRR") }}</span>
              </th>
              <th v-else-if="header.id == 'delta_percentage'" class="px-3 py-3 whitespace-nowrap">
                <span class="font-medium text-gray-400">{{ $t("Delta%") }}</span>
              </th>
              <th v-else-if="header.id == 'delta'" class="px-3 py-3 whitespace-nowrap">
                <span class="font-medium text-gray-400">{{ $t("Delta") }}</span>
              </th>
              <th v-else-if="header.id == 'Flag'" class="px-3 py-3 whitespace-nowrap">
                <span class="font-medium text-gray-400">{{ $t("Flag") }}</span>
              </th>
              <th v-else class="" />
            </template>
          </tr>
        </thead>
        <tbody
          v-if="records_forecast.length > 0"
          class="transition ease-in-out divide-y bg-light-box-background dark:bg-dark-box-background divide-light-divider-border dark:divide-dark-divider-border duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text">
          <tr
            v-for="record_forecast in records_forecast"
            :id="record_forecast.record.id"
            :key="record_forecast.id"
            class="rounded-lg text-bold">
            <template v-for="header in headers.concat({ id: '_action' })" :key="header.id + record_forecast.id">
              <td
                v-if="header.id == 'account'"
                class="flex items-center px-3 py-2 tracking-wider bg-white dark:bg-dark-box-background freeze-column group/account min-w-[500px] w-[500px]">
                <div
                  class="relative w-full py-2 pl-3 pr-10 text-left rounded-md cursor-default focus:outline-none focus:ring-1 focus:ring-blue-light focus:border-blue-light">
                  <div
                    class="flex items-center hover:cursor-pointer"
                    @click="isEndUser ? null : $utils.Record(record_forecast.record).open()">
                    <!-- The classes are binded this way because of the tailwind jit  -->
                    <span
                      :class="{
                        'bg-status-success-green': confirmCompanyForecastColor(record_forecast, 'green'),
                        'bg-status-warning-yellow': confirmCompanyForecastColor(record_forecast, 'yellow'),
                        'bg-status-error-red': confirmCompanyForecastColor(record_forecast, 'red'),
                        'bg-orange-clear': confirmCompanyForecastColor(record_forecast, 'orange'),
                        'text-gray-300': confirmCompanyForecastColor(record_forecast, 'gray')
                      }"
                      class="flex-shrink-0 inline-block w-3 h-3 rounded-full" />

                    <span class="block ml-3 truncate">{{ Record.from(record_forecast.record)?.name }}</span>
                    <div
                      v-if="!editing_disabled || (editing_disabled && record_forecast.notes_summary.count)"
                      class="relative ml-1 -mt-4"
                      @click.stop="openModalNotes(record_forecast)">
                      <NotesIcon
                        :class="[
                          record_forecast.notes_summary.count == 0 ? 'opacity-0' : 'opacity-100',
                          'group-hover/account:opacity-100 mr-2 w-5 h-5'
                        ]" />
                      <div class="flex justify-center items-center absolute left-0.5 top-0">
                        <div class="flex items-center justify-center w-4 h-4 text-xs font-bold text-center text-white rounded-full">
                          <Plus
                            v-if="record_forecast.notes_summary.count == 0"
                            class="w-5 h-5 transition opacity-0 group-hover/account:opacity-100" />
                          <div v-else>
                            {{ record_forecast.notes_summary.count }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td
                v-else-if="header.id == 'owner'"
                :class="{
                  'freeze-column bg-white dark:bg-dark-box-background group-hover/account:bg-light-brand-blue dark:group-hover/account:bg-dark-brand-blue transition ease-in-out ':
                    $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '_owner')
                }"
                class="px-6 py-3 font-light min-w-[250px]">
                <div class="flex items-center">
                  <img
                    v-if="$utils.getUserById(record_forecast.record.owner, 'avatar')"
                    class="inline-block rounded-full h-9 w-9 object-cover"
                    onerror="this.onerror=null; this.src=window.default_avatar;"
                    :src="$utils.getUserById(record_forecast.record.owner, 'avatar')"
                    alt="">
                  <Avatar
                    v-else
                    :hover-effect="true"
                    :first-name="$utils.getUserById(record_forecast.record.owner, 'first_name')"
                    :last-name="$utils.getUserById(record_forecast.record.owner, 'last_name')" />
                  <div class="ml-3">
                    <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/account:text-white">
                      {{ $utils.getUserById(record_forecast.record.owner, "full_name") }}
                    </p>
                  </div>
                </div>
              </td>
              <td
                v-else-if="header.id == 'co_owners'"
                :class="{
                  'freeze-column bg-white dark:bg-dark-box-background group-hover/owner:bg-light-brand-blue dark:group-hover/account:bg-dark-brand-blue transition ease-in-out':
                    $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '_co-owners')
                }"
                class="px-6 py-3 font-light min-w-[250px]">
                <div class="flex items-center space-x-2">
                  <div class="flex flex-shrink-0 -space-x-2">
                    <span v-for="user in record_forecast.record.co_owners.slice(0, 5)" :key="user" class="inline-block has-tooltip">
                      <img
                        v-if="$utils.getUserById(user, 'avatar')"
                        class="inline-block w-8 h-8 rounded-full"
                        onerror="this.onerror=null; this.src=window.default_avatar;"
                        :src="$utils.getUserById(user, 'avatar')"
                        alt="">
                      <Avatar
                        v-else
                        :hover-effect="true"
                        :first-name="$utils.getUserById(user, 'first_name')"
                        :last-name="$utils.getUserById(user, 'last_name')" />

                      <span class="p-1 bg-gray-300 rounded-sm tooltip">
                        <div class="">
                          <p class="text-sm font-medium text-gray-700 group-hover/account:text-gray-900">
                            {{ $utils.getUserById(user, "full_name") }}
                          </p>
                        </div>
                      </span>
                    </span>
                  </div>
                  <span v-if="record_forecast.record.co_owners.length > 5"
                        class="flex-shrink-0 text-xs font-medium leading-5">+{{ record_forecast.record.co_owners.length - 5 }}</span>
                </div>
              </td>
              <td v-else-if="header.id == 'tags'" class="px-3 overflow-visible tracking-wider text-bold whitespace-nowrap group/edittag">
                <div class="flex">
                  <div v-if="record_forecast.record.tags?.length" class="flex flex-row gap-1 flex-nowrap">
                    <div
                      v-for="tag in record_forecast.record?.is_all_assigned_tags_shown
                        ? record_forecast.record?.tags
                        : record_forecast.record?.tags?.slice(0, 2)"
                      :key="tag"
                      class="flex justify-between px-2 py-2 pl-3 text-left rounded-lg dark:text-dark-body-text focus:outline-none"
                      :style="{
                        background: `${$store.state.data.tags.find((t) => t.id == tag)?.color}0F`
                      }">
                      <span class="flex items-center mr-1">
                        <span
                          class="mr-2 w-3.5 h-3.5 rounded-full"
                          :style="{
                            background: $store.state.data.tags.find((t) => t.id == tag)?.color
                          }" />
                        <span
                          class="block font-bold truncate"
                          :style="{
                            color: $store.state.data.tags.find((t) => t.id == tag)?.color
                          }">{{ $store.state.data.tags.find((t) => t.id == tag)?.name }}</span>
                      </span>
                    </div>
                    <div
                      v-if="record_forecast.record?.tags?.length > 2 && !record_forecast.record?.is_all_assigned_tags_shown"
                      class="flex items-center flex-none mr-1 font-normal group/tags flex-nowrap dark:text-white">
                      <span class="block group-hover/tags:hidden">+{{ record_forecast.record?.tags?.length - 2 }} more</span>
                      <div class="relative" @click.stop="showAllAssignedTags(record_forecast)">
                        <transition
                          class="hidden group-hover/tags:block"
                          mode="out-in"
                          enter-active-class="animate__animated animate__fadeIn"
                          leave-active-class="animate__animated animate__fadeOut"
                          duration="100">
                          <button
                            class="z-50 flex items-center justify-center w-8 h-8 ml-3 transition-all rounded-full opacity-0 group-hover/tags:delay-300 group-hover/tags:duration-300 group-hover/tags:opacity-100 group-hover/tags:scale-150 group-hover/tags:bg-gray-300">
                            <ChevronsRight
                              class="absolute inset-0 w-5 h-5 m-auto text-white"
                              :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
                          </button>
                        </transition>
                      </div>
                    </div>
                    <div
                      v-else-if="record_forecast.record?.tags?.length > 2 && record_forecast.record?.is_all_assigned_tags_shown"
                      class="flex items-center flex-none mr-1 font-normal flex-nowrap dark:text-white"
                      @click.stop="showAllAssignedTags(record_forecast)">
                      <div class="relative">
                        <transition
                          mode="out-in"
                          enter-active-class="animate__animated animate__fadeIn"
                          leave-active-class="animate__animated animate__fadeOut"
                          duration="100">
                          <button
                            class="z-50 flex items-center justify-center w-8 h-8 ml-3 transition-all duration-300 delay-300 scale-150 bg-gray-300 rounded-full">
                            <ChevronsLeft
                              class="absolute inset-0 w-5 h-5 m-auto text-white"
                              :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
                          </button>
                        </transition>
                      </div>
                    </div>
                  </div>
                  <div v-else class="flex flex-row items-center flex-none gap-1 ml-2 flex-nowrap">
                    -
                  </div>
                  <div class="flex items-center">
                    <transition
                      mode="out-in"
                      enter-active-class="animate__animated animate__fadeIn"
                      leave-active-class="animate__animated animate__fadeOut"
                      duration="100">
                      <button
                        class="z-20 flex items-center justify-center w-8 h-8 ml-6 transition-all rounded-full opacity-0 group-hover/edittag:delay-300 group-hover/edittag:duration-300 group-hover/edittag:opacity-100 group-hover/edittag:scale-150 group-hover/edittag:bg-gray-300"
                        @click.stop="
                          $emitter.emit('assignTags', {
                            full_records: null,
                            records: null,
                            record: record_forecast.record,
                            object_type: objectType
                          })
                        ">
                        <Pencil v-if="record_forecast.record.tags?.length" color="#FFF" class="w-4 h-4" aria-hidden="true" />
                        <Plus v-else class="w-4 h-4 font-bold text-white" aria-hidden="true" />
                      </button>
                    </transition>
                  </div>
                </div>
              </td>
              <td v-else-if="header.id == 'forecast_type'" class="px-3 overflow-visible tracking-wider text-bold whitespace-nowrap">
                <select
                  v-model="record_forecast.forecast_type"
                  :disabled="editing_disabled"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  @change="updateRecordForecast(record_forecast, 'forecast_type')">
                  <option
                    v-for="option in forecast_types"
                    :key="option.name"
                    :value="option.value"
                    :class="[
                      record_forecast.forecast_type == option.value ? 'bg-light-brand-blue dark:bg-dark-brand-blue' : '',
                      'appearance-none relative cursor-default py-2 pl-8 pr-4'
                    ]">
                    {{ option.name }}
                  </option>
                </select>
              </td>
              <td v-else-if="header.id == 'forecasted_date'" class="px-3 overflow-visible text-bold">
                <DatePicker
                  v-model="record_forecast.forecasted_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  mode="date"
                  :popover="{
                    visibility: record_forecast.forecast_type != 'none' ? 'click' : 'none'
                  }"
                  @dayclick="updateRecordForecast(record_forecast, 'forecasted_date')">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative flex-grow w-full">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute top-0 w-4 h-full mx-2 font-thin text-gray-600 transition ease-in-out transform pointer-events-none right-2 duration-side-menu-transition-duration">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        :disabled="editing_disabled"
                        :value="inputValue"
                        placeholder="Renewal date"
                        :class="{
                          'bg-gray-100 dark:bg-dark-box-background': record_forecast.forecast_type == 'none'
                        }"
                        class="flex-grow pr-2 border rounded-lg w-full min-w-[200px] dark:bg-dark-box-background px-2 py-2 text-light-body-text dark:text-dark-body-text outline-none border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                        v-on="inputEvents">
                    </div>
                  </template>
                </DatePicker>
              </td>
              <td v-else-if="header.id == 'original_base_value'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="
                    !record_forecast.record.values[mapped_metric_reference] && record_forecast.record.values[mapped_metric_reference] != 0
                  "
                  class="inline-flex items-center mt-4 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-none dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration">
                  {{ $utils.formatAsCurrency(record_forecast.record.values[mapped_metric_reference]) }}
                </span>
              </td>
              <td v-else-if="header.id == 'base_value'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <input
                  :disabled="editing_disabled"
                  :value="`${$utils.formatAsCurrency(parseInt(record_forecast.renewal_base_value))}`"
                  type="text"
                  :placeholder="mapped_metric_reference && record_forecast.record.values[mapped_metric_reference]"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  :class="{
                    'bg-red-50 dark:bg-dark-box-background': record_forecast.renewal_base_value == 0,
                    'pointer-events-none opacity-40': updating_forecast == 'base_value'
                  }"
                  @focusout="updateRecordForecast(record_forecast, 'base_value')"
                  @input="
                    $utils.debounce(() => {
                      if ($event.target.value != record_forecast.renewal_base_value) {
                        record_forecast.renewal_base_value = $event.target.value.replace('$', '').replace('%', '').replaceAll(',', '')
                        //updateRecordForecast(record_forecast)
                      }
                    }, 100)
                  ">
              </td>
              <td v-else-if="header.id == 'forecast_status'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <select
                  v-model="record_forecast.forecast_status"
                  :disabled="editing_disabled"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  @change="updateRecordForecast(record_forecast, 'forecast_status')">
                  <option
                    v-for="option in forecast_statuses"
                    :key="option.name"
                    :value="option.value"
                    :class="[
                      record_forecast.forecast_type == option.value ? 'bg-light-brand-blue dark:bg-dark-brand-blue' : '',
                      'appearance-none relative cursor-default py-2 pl-8 pr-4'
                    ]">
                    {{ option.name }}
                  </option>
                </select>
              </td>
              <td v-else-if="header.id == 'forecasted_value'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <input
                  :disabled="editing_disabled"
                  :value="`${$utils.formatAsCurrency(parseInt(record_forecast.forecasted_value))}`"
                  type="text"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  :class="{
                    'bg-red-50 dark:bg-dark-box-background': record_forecast.forecasted_value == 0,
                    'pointer-events-none opacity-40': updating_forecast == 'forecasted_value'
                  }"
                  @focusout="updateRecordForecast(record_forecast, 'forecasted_value')"
                  @input="
                    $utils.debounce(() => {
                      if ($event.target.value != record_forecast.forecasted_value) {
                        record_forecast.forecasted_value = $event.target.value.replace('$', '').replace('%', '').replaceAll(',', '')
                        //updateRecordForecast(record_forecast)
                      }
                    }, 100)
                  ">
              </td>
              <td v-else-if="header.id == 'forecasted_value_max'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <input
                  :disabled="editing_disabled"
                  :value="`${$utils.formatAsCurrency(parseInt(record_forecast.forecasted_value_max))}`"
                  type="text"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  :class="{
                    'bg-red-50 dark:bg-dark-box-background': record_forecast.forecasted_value_max == 0,
                    'pointer-events-none opacity-40': updating_forecast == 'forecasted_value_max'
                  }"
                  @focusout="updateRecordForecast(record_forecast, 'forecasted_value_max')"
                  @input="
                    $utils.debounce(() => {
                      if ($event.target.value != record_forecast.forecasted_value_max) {
                        record_forecast.forecasted_value_max = $event.target.value.replace('$', '').replace('%', '').replaceAll(',', '')
                        //updateRecordForecast(record_forecast)
                      }
                    }, 100)
                  ">
              </td>
              <td v-else-if="header.id == 'forecasted_value_min'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <input
                  :disabled="editing_disabled"
                  :value="`${$utils.formatAsCurrency(parseInt(record_forecast.forecasted_value_min))}`"
                  type="text"
                  class="min-w-[150px] appearance-none text-left block w-full py-2 border border-light-divider-border dark:border-dark-divider-border rounded-lg bg-light-box-background dark:bg-dark-box-background focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                  :class="{
                    'bg-red-50 dark:bg-dark-box-background': record_forecast.forecasted_value_min == 0,
                    'pointer-events-none opacity-40': updating_forecast == 'forecasted_value_min'
                  }"
                  @focusout="updateRecordForecast(record_forecast, 'forecasted_value_min')"
                  @input="
                    $utils.debounce(() => {
                      if ($event.target.value != record_forecast.forecasted_value_min) {
                        record_forecast.forecasted_value_min = $event.target.value.replace('$', '').replace('%', '').replaceAll(',', '')
                        //updateRecordForecast(record_forecast)
                      }
                    }, 100)
                  ">
              </td>
              <td v-else-if="header.id == 'grr'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="!record_forecast.stats.grr && record_forecast.stats.grr != 0"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span v-else>
                  {{ record_forecast.stats.grr }}
                </span>
              </td>
              <td v-else-if="header.id == 'nrr'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="!record_forecast.stats.nrr && record_forecast.stats.nrr != 0"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span v-else>
                  {{ record_forecast.stats.nrr }}
                </span>
              </td>
              <td v-else-if="header.id == 'flag'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="!record_forecast.stats.flag"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span v-else class="capitalize">
                  {{ record_forecast.stats.flag }}
                </span>
              </td>
              <td v-else-if="header.id == 'delta_percentage'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="!record_forecast.stats.delta_percentage && record_forecast.stats.delta_percentage != 0"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span v-else>
                  {{ record_forecast.stats.delta_percentage }}
                </span>
              </td>
              <td v-else-if="header.id == 'delta'" class="px-3 py-2 tracking-wider text-bold whitespace-nowrap">
                <span
                  v-if="!record_forecast.stats.delta && record_forecast.stats.delta != 0"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-dark-screen-background text-gray-800 dark:text-white">
                  {{ $t("blank") }}
                </span>
                <span v-else>
                  {{ record_forecast.stats.delta }}
                </span>
              </td>
              <td v-else class="px-2 text-bold">
                <input
                  v-if="!editing_disabled"
                  v-model="record_forecast.record.checked"
                  :checked="record_forecast.record.checked"
                  type="checkbox"
                  class="form-checkbox duration-75 text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5"
                  @click="toggleRecordFromSelectedList(record_forecast, !record_forecast.record.checked)">
              </td>
            </template>
          </tr>
        </tbody>
      </table>
      <div
        v-if="records_forecast.length == 0"
        class="w-full divide-y divide-gray-200 bg-light-screen-background dark:bg-dark-screen-background">
        <div class="items-center justify-center w-full h-80">
          <div class="flex justify-center w-full h-full bg-light-box-background dark:bg-dark-box-background">
            <div class="flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-20 h-20 mx-auto text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
              </svg>

              <h3 class="mt-2 text-2xl font-normal text-gray-500">
                {{ $t("No Forecasted Cases in this range") }}
              </h3>
              <span class="mt-2 text-base font-normal text-gray-400">
                {{ $t("Try to select a different range or add a case to your forecast") }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="search_is_on" class="divide-y divide-gray-200 bg-light-screen-background dark:bg-dark-screen-background">
        <div class="items-center justify-center w-full h-80">
          <div class="flex justify-center h-full bg-white border shadow">
            <div class="flex flex-col items-center justify-center">
              <svg class="mr-3 -ml-1 animate-spin h-14 w-14 text-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4" />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>

              <h3 class="mt-2 text-lg font-normal text-gray-500">
                {{ $t("Searching for") }}
                <span class="font-semibold capitalize"> {{ forecast_object_type }}</span>
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="w-full bg-white dark:bg-dark-box-background">
      <div class="absolute bottom-0 z-10 w-full pt-2 overflow-visible bg-white border-t h-14 dark:bg-dark-box-background">
        <PaginationFooter :min-page-size="10" :default-page-size="10" :object="records_forecast_pagination" @update="updatePage" />
      </div>
    </div>

    <div
      v-if="!search_is_on && filtered_companies.length == 0 && companies_search_text != ''"
      class="divide-y divide-gray-200 bg-light-box-backround dark:bg-dark-box-background">
      <div class="items-center justify-center w-full h-80">
        <div
          class="flex justify-center h-full transition ease-in-out border shadow duration-side-menu-transition-duration bg-light-box-backround dark:bg-dark-box-background">
          <div class="flex flex-col justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-20 h-20 mx-auto text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
            </svg>

            <h3 class="mt-2 text-2xl font-normal text-gray-500">
              {{ $t("No companies found") }}
            </h3>
          </div>
        </div>
      </div>
    </div>
    <Modal
      v-model:is-open="remove_company_modal_is_open"
      :default-success-msg="`Records removed successfully`"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Remove records") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to remove these selected records from the forecast list?") }}</p>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">Cancel</button>
          <button type="button" class="danger-btn w-fit" @click="removeCompaniesFromForecast()">{{ $t("Delete") }}</button>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="hide_company_modal_is_open"
      :default-success-msg="$t('Records hided successfully')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Hide records") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Please select the users you want to hide these selected records from the forecast list") }}</p>
          <TeamMembersSelect v-model:selected-team-members="selected_users" :team-members="selected_users" :label="$t('Team Members')" />
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="danger-btn w-fit" @click="hideCompaniesFromForecast()">{{ $t("Submit") }}</button>
        </div>
      </template>
    </Modal>

    <Modal
      v-model:is-open="show_hidden_company_modal_is_open"
      :default-success-msg="$t('Records hided successfully')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Show Hidden records") }}
        </h3>
        <div class="max-w-xl mb-2 text-gray-500 text-md">
          <p v-if="hidden_records_forecast.length == 0">
            {{ $t("There is no hidden records from the forecast list") }}
          </p>
          <p v-else>
            {{ $t("Please select the records you want to show them from the forecast list") }}
          </p>
        </div>
        <div
          v-for="(record, i) in hidden_records_forecast"
          :key="record.id"
          :class="{ 'border-b': i < hidden_records_forecast.length - 1 }"
          class="pb-2 mb-4">
          <div class="flex items-center text-light-body-text dark:text-dark-body-text">
            <input
              :checked="selected_hidden_records_forecast.includes(record.id)"
              type="checkbox"
              class="ml-2 mr-2 mt-2 form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
              @input="(e) => handleSelectHiddenRecord(e, record)">

            <div>
              <div class="block mt-1 ml-3 font-normal truncate">
                {{ $utils.getRecordName(record.record) }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button v-if="hidden_records_forecast.length" type="button" class="primary-btn w-fit" @click="ShowHiddenCompaniesFromForecast()">
            {{ $t("Submit") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Add company to forecasted companies modal -->
    <Modal v-model:is-open="add_company_modal_is_open" class="divide-y divide-red-900">
      <template #form="{}">
        <h3
          class="text-xl border-b-[1px] border-light-divider-border dark:border-dark-divider-border pb-3 -mt-3 font-medium dark:text-dark-body-text text-gray-800 text-left">
          {{ $t("Add") }} <span class="font-semibold capitalize"> {{ forecast_object_type }}</span>
        </h3>
        <RecordAutoCompleteInput
          v-model="company_search_text"
          :object-type="forecast_object_type"
          :owners-filter="selected_team_members"
          :filter-metric-ids="[object_type_name_metric]"
          :is-async="true"
          :show-success-check-mark="false"
          :placeholder="$t('Search for existing') + ` ${forecast_object_type}`"
          :is-box-background="true"
          label=""
          name="search"
          type="text"
          classes="w-full block border-none pl-0"
          @chosen="
            ($event) => {
              to_be_forecasted_company = $event
            }
          " />

        <div class="flex justify-center float-right gap-3 mt-5 sm:justify-end sm:mt-6">
          <button type="button" class="cancel-btn" @click="add_company_modal_is_open = false">{{ $t("Cancel") }}</button>
          <button
            :disabled="to_be_forecasted_company == null"
            type="button"
            class="primary-btn"
            @click="addCompany(to_be_forecasted_company)">
            {{ $t("Add") }}
            <span class="pl-1 font-semibold capitalize"> {{ forecast_object_type }}</span>
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="jsx">
import draggable from "vuedraggable"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import {
  ListFilter,
  ChevronDown,
  ChevronsRight,
  ChevronRight,
  Search,
  X,
  Plus,
  User,
  Users,
  TrendingUp,
  Pencil,
  Layers,
  Tag,
  TrendingDown,
  ChevronsLeft,
  Grid2x2Plus,
  ListTodo,
  PencilLine,
  ListCollapse,
  CalendarDays
} from "lucide-vue-next"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import { Record } from "@/models/record"
import dataService from "@/services/data.service"
import BigKpi from "@/components/shared/BigKpi.vue"
import MetricSummary from "@/components/shared/rollup-metrics/RollUpMetric.vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import SelectDropDownMenu from "@/components/shared/SelectDropDownMenu.vue"
import SelectGroupDropDownMenu from "@/components/shared/SelectGroupDropDownMenu.vue"
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"
import { VIEW_TYPES, PAGE_REFERENCES } from "@/store/constants/references"
import Avatar from "@/components/shared/Avatar.vue"
import { markRaw } from "vue"
import NotesIcon from "@/components/shared/icons/NotesIcon.vue"
import RecordActionsDropDown from "@/components/shared/RecordActionsDropDown.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import QuarterDatePicker from "@/components/shared/QuarterDatePicker.vue"
import UpdateRecord from "@/components/shared/maps-components/UpdateRecord.vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import PaginationFooter from "@/components/shared/PaginationFooter.vue"

export default {
  components: {
    draggable,
    ListFilter,
    RecordActionsDropDown,
    Grid2x2Plus,
    TrendingDown,
    NotesIcon,
    Tag,
    Pencil,
    ChevronsRight,
    ChevronsLeft,
    PencilLine,
    TrendingUp,
    Layers,
    Users,
    User,
    CalendarDays,
    ListCollapse,
    Avatar,
    TeamMembersSelect,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    RecordAutoCompleteInput,
    DatePicker,
    TextInput,
    Modal,
    Search,
    X,
    ChevronDown,
    Plus,
    BigKpi,
    MetricSummary,
    MultiSelectInput,
    SelectDropDownMenu,
    SelectGroupDropDownMenu,
    QuarterDatePicker,
    UpdateRecord,
    ChevronRight,
    ComboBoxInput,
    PaginationFooter,
    ListTodo
  },
  inject: ["getViewMetrics", "canUserEditLayout"],
  props: {
    view_by: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    objectType: {
      type: [Object, String],
      required: false
    },
    componentReference: {
      type: String,
      required: false
    },
    forecast_name: {
      type: String,
      required: true
    },
    itemLayoutWidth: {
      type: Number,
      default: null
    }
  },
  emits: [
    "selectedOwners",
    "update:selectedOwners",
    "update:selectedTags",
    "update:selectedGroup",
    "update:selectedDateRange",
    "updateRollups",
    "dbclick",
    "update:selectedCoOwners"
  ],
  data() {
    return {
      ALL_EMITTERS,
      first_loading: false,
      user_menu_icon: User,
      users_menu_icon: Users,
      dates_icon: markRaw(CalendarDays),
      types_icon: markRaw(ListCollapse),
      status_icon: markRaw(ListTodo),
      name_pencil_icon: markRaw(PencilLine),
      Record,
      PAGE_REFERENCES,
      is_loading: false,
      search_is_on: false,
      to_be_forecasted_company: null,
      company_search_text: "",
      add_company_modal_is_open: false,
      remove_company_modal_is_open: false,
      hide_company_modal_is_open: false,
      show_hidden_company_modal_is_open: false,
      selected_users: [this.$store.state.auth.user],
      selected_group: null,
      headers: [],
      drag: false,
      is_filter_open: false,
      is_input_base_value: false,
      is_input_forecasted_value: false,
      is_input_forecasted_value_min: false,
      is_input_forecasted_value_max: false,
      search_term: "",
      forecast_status: null,
      forecast_type: null,
      flag: null,
      base_value_gte: null,
      base_value_lte: null,
      forecasted_value_gte: null,
      forecasted_value_lte: null,
      forecasted_value_min_gte: null,
      forecasted_value_min_lte: null,
      forecasted_value_max_gte: null,
      forecasted_value_max_lte: null,
      forecast_date_range: null,
      // forecast_stats: [],
      forecast_stats_with_value: [],
      // forecast_stats_without_value: [],
      forecast_types: [
        { value: "non-renewable", name: this.$t("Non-Renewable") },
        { value: "renewal", name: this.$t("Renewal") },
        { value: "expansion", name: this.$t("Expansion") },
        { value: "influx", name: this.$t("Influx") }
      ],
      flags: [
        { value: "churn", name: this.$t("Churn") },
        { value: "non-renewable", name: this.$t("Non-Renewable") },
        { value: "renewal", name: this.$t("Renewal") },
        { value: "expansion", name: this.$t("Expansion") },
        { value: "influx", name: this.$t("Influx") }
      ],
      forecast_statuses: [
        { value: "forecasted", name: this.$t("Forecasted") },
        { value: "actual", name: this.$t("Actual") }
      ],
      //Filter panels

      forecast_object_type: null,
      forecast_object_type_id: null,
      forecast_obj: null,
      // smart table outputs
      records_forecast: [],
      hidden_records_forecast: [],
      selected_hidden_records_forecast: [],
      selected_records: new Set(),
      selected_records_forecast: new Set(),
      selected_full_records: new Set(),
      select_all_records: false,
      records_forecast_pagination: { count: 0, results: [], next: null, previous: null },
      filter_selected_co_owners: [],
      filter_selected_owners: [],
      filter_selected_tags: [],
      selected_team_members: [],
      displayed_metrics: [],
      all_headers: [
        {
          label: this.$t("Account"),
          id: "account",
          class: "px-3 py-3 whitespace-nowrap font-light bg-white dark:bg-dark-box-background freeze-column",
          icon: "PencilLine"
        },
        {
          label: this.$t("Owner"),
          id: "owner",
          class: "px-3 py-3 whitespace-nowrap font-light ",
          icon: "User"
        },
        {
          label: this.$t("Co-owners"),
          id: "co_owners",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "Users"
        },
        {
          label: this.$t("Tags"),
          id: "tags",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "Tag"
        },
        {
          label: this.$t("Type"),
          id: "forecast_type",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "ListCollapse"
        },
        {
          label: this.$t("Date"),
          id: "forecasted_date",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "CalendarDays"
        },
        {
          label: this.$t("Base Value"),
          id: "base_value",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "Layers"
        },
        {
          label: this.$t("Original Base"),
          id: "original_base_value",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "Layers"
        },
        {
          label: this.$t("Status"),
          id: "forecast_status",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "ListTodo"
        },
        {
          label: this.$t("Forecasted Value"),
          id: "forecasted_value",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "TrendingUp"
        },
        {
          label: this.$t("Best Case"),
          id: "forecasted_value_max",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "TrendingUp"
        },
        {
          label: this.$t("Worst Case"),
          id: "forecasted_value_min",
          class: "px-3 py-3 whitespace-nowrap font-light",
          icon: "TrendingDown"
        },
        {
          label: this.$t("GRR"),
          id: "grr",
          class: "px-3 py-3 whitespace-nowrap font-light"
        },
        {
          label: this.$t("NRR"),
          id: "nrr",
          class: "px-3 py-3 whitespace-nowrap font-light"
        },
        {
          label: this.$t("Delta%"),
          id: "delta_percentage",
          class: "px-3 py-3 whitespace-nowrap font-light"
        },
        {
          label: this.$t("Delta"),
          id: "delta",
          class: "px-3 py-3 whitespace-nowrap font-light"
        },
        {
          label: this.$t("Flag"),
          id: "flag",
          class: "px-3 py-3 whitespace-nowrap font-light"
        }
      ],
      VIEW_TYPES,
      forecasted_objects: [],

      companies_search_text: "",
      filtered_companies: [],
      forecast_records: [],
      current_object: null,
      selected_record: null,
      current_forecast: null,
      user_forecasts: [],
      update_record_modal_is_open: false,
      selected_range: null,
      updating_forecast: null
    }
  },
  computed: {
    groups() {
      let object_type_calc = this.objectType
      if (this.$store.state.object_types.find((o) => o.id == this.objectType)) {
        object_type_calc = this.$store.state.object_types.find((o) => o.id == this.objectType).type
      }
      return [{ id: null, name: this.$t("All Accounts"), value: null, type: this.current_object?.type }].concat(
        this.$store.state.data?.groups.filter((group) => (object_type_calc ? group.type == object_type_calc : true))
      )
    },
    isEndUser() {
      return this.$store.state.auth.user.user_type == "end_user"
    },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },
    mapped_metric_reference() {
      return utils.getMetricById(
        utils
          .find(this.$store.state.settings.modules_settings, {
            reference_code: "forecasting_mappings"
          })
          ?.forecasting_mappings.find((f) => f.id == this.current_forecast?.forecast_settings)?.renewal_base_metric
      )?.reference_name
    },
    forecasting_breads() {
      return [
        {
          id: 1,
          is_component: true,
          component: (
            <SelectDropDownMenu
              v-model:current-item={this.current_object}
              items={this.forecasted_objects}
              classes="w-full relative dark:text-white"
              name="forecast-object-type"
              label-attr="label"
              placeholder="Object"
              is-box-background={true}
              label=""
              disable-input={true}
              show-label={false}
              floating-label={false}
              allow-null={false}
            />
          )
        },
        {
          id: 2,
          is_component: true,
          component: (
            <SelectDropDownMenu
              v-model:current-item={this.current_forecast}
              items={this.user_forecasts.filter((x) => x.type == this.current_object?.type)}
              classes="w-full min-w-[300px] relative dark:text-white"
              inputClasses="text-left"
              placeholder="Forecast"
              name="forecast-object"
              label-attr="name"
              is-box-background={true}
              label=""
              show-label={false}
              floating-label={false}
              allow-null={false}
            />
          )
        }
      ]
    },
    current_headers() {
      if (this.$store.state.ui_options.forecast_table_headers) {
        return this.$store.state.ui_options.forecast_table_headers[this.componentReference]
      }
      return []
    },
    filters() {
      let filters = { page_size: 10 }
      if (this.search_term) {
        filters["record_name"] = this.search_term
      }
      if (this.forecast_type) {
        filters["forecast_type"] = this.forecast_type.join(",")
      }
      if (this.flag) {
        filters["flag"] = this.flag.join(",")
      }

      if (this.forecast_date_range) {
        filters["from_date"] = this.$moment(this.forecast_date_range?.start).format("YYYY-MM-DD")
        filters["to_date"] = this.$moment(this.forecast_date_range?.end).format("YYYY-MM-DD")
      } else {
        filters["from_date"] = this.$moment(this.selected_range?.start).format("YYYY-MM-DD")
        filters["to_date"] = this.$moment(this.selected_range?.end).format("YYYY-MM-DD")
      }
      if (this.selected_range) {
        filters["start_date"] = this.selected_range?.start.format("L")
        filters["end_date"] = this.selected_range?.end.format("L")
      }
      if (this.base_value_gte) {
        filters["base_value_gte"] = this.base_value_gte
      }
      if (this.base_value_lte) {
        filters["base_value_lte"] = this.base_value_lte
      }
      if (this.forecast_status) {
        filters["forecast_status"] = this.forecast_status.join(",")
      }
      if (this.forecasted_value_gte) {
        filters["forecasted_value_gte"] = this.forecasted_value_gte
      }
      if (this.forecasted_value_lte) {
        filters["forecasted_value_lte"] = this.forecasted_value_lte
      }
      if (this.forecasted_value_min_gte) {
        filters["forecasted_value_min_gte"] = this.forecasted_value_min_gte
      }
      if (this.forecasted_value_min_lte) {
        filters["forecasted_value_min_lte"] = this.forecasted_value_min_lte
      }
      if (this.forecasted_value_max_gte) {
        filters["forecasted_value_max_gte"] = this.forecasted_value_max_gte
      }
      if (this.forecasted_value_max_lte) {
        filters["forecasted_value_max_lte"] = this.forecasted_value_max_lte
      }
      if (this.filter_selected_co_owners?.length) {
        filters["co_owners"] = this.filter_selected_co_owners.join(",")
        this.$emit("update:selectedCoOwners", this.filter_selected_co_owners)
      } else {
        this.$emit("update:selectedCoOwners", null)
      }
      if (this.filter_selected_owners?.length) {
        filters["owners"] = this.filter_selected_owners.join(",")
        this.$emit("update:selectedOwners", this.filter_selected_owners)
      } else {
        this.$emit("update:selectedOwners", null)
      }
      if (this.filter_selected_tags?.length) {
        filters["tags"] = this.filter_selected_tags.join(",")
        this.$emit("update:selectedTags", this.filter_selected_tags)
      } else {
        this.$emit("update:selectedTags", null)
      }
      if (this.selected_group?.id) {
        this.$emit("update:selectedGroup", this.selected_group)
        filters["group"] = this.selected_group.id
      } else {
        this.$emit("update:selectedGroup", null)
      }
      return filters
    },
    object_type_name_metric() {
      return utils.getMetricByReferenceName(utils.getOptionMetricReference(this.forecast_object_type, "name"))?.id
    }
  },

  watch: {
    view_by() {
      this.getCurrentForecastCompanies()
    },
    forecast_obj() {
      this.getForecasts()
    },
    current_forecast() {
      //this.findForecastCompaniesByFilters()
    },
    current_object() {
      if (this.current_object) {
        //switch to first object forecast if object changed
        this.current_forecast = this.user_forecasts.filter((x) => x.type == this.current_object?.type)[0]
      } else {
        this.current_forecast = null
      }
    },
    companies_search_text() {
      this.search_is_on = true
      let filters = { search: this.companies_search_text }
      dataService.getCompanies(filters).then((result) => {
        this.search_is_on = false
        this.filtered_companies = result.data.results
      })
    },
    forecast_stats_with_value: {
      handler() {
        this.forecast_stats_with_value.forEach((metric) => {
          this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRIC, { metric: metric })
        })
      }
    },
    selected_range: {
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "selectedDateRange",
          data: {
            from_date: this.$moment(this.selected_range.start).format("YYYY-MM-DD"),
            to_date: this.$moment(this.selected_range.end).format("YYYY-MM-DD")
          }
        })
        this.getCurrentForecastCompanies()
      }
    },
    search_term: {
      deep: true,
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "searchRecordTerm",
          data: this.search_term
        })
        this.getCurrentForecastCompanies()
      }
    },
    forecast_status: {
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "forecastStatuses",
          data: this.filters["forecast_status"]
        })
        this.getCurrentForecastCompanies()
      }
    },
    forecast_type: {
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "forecastTypes",
          data: this.filters["forecast_type"]
        })
        this.getCurrentForecastCompanies()
      }
    },
    flag: {
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "forecastFlags",
          data: this.filters["flag"]
        })
        this.getCurrentForecastCompanies()
      }
    },
    forecast_date_range: {
      handler() {
        this.getCurrentForecastCompanies()
      }
    },
    filter_selected_co_owners() {
      this.$emitter.emit(this.componentReference, {
        event: "selectedCoOwners",
        data: this.filters["co_owners"]
      })
      this.getCurrentForecastCompanies()
    },
    filter_selected_owners() {
      this.$emitter.emit(this.componentReference, {
        event: "selectedOwners",
        data: this.filters["owners"]
      })
      this.getCurrentForecastCompanies()
    },
    filter_selected_tags() {
      this.$emitter.emit(this.componentReference, {
        event: "selectedTags",
        data: this.filters["tags"]
      })
      this.getCurrentForecastCompanies()
    },
    selected_group: {
      deep: true,
      handler() {
        this.getCurrentForecastCompanies()
      }
    },
    current_headers() {
      this.refreshTableView()
    },
    select_all_records() {
      this.records_forecast = this.records_forecast.map((m) => {
        m.record["checked"] = this.select_all_records
        return m
      })

      if (this.select_all_records) {
        this.records_forecast.forEach((e) => {
          this.selected_records.add(e.record.id)
          this.selected_full_records.add(e.record)
          this.selected_records_forecast.add(e.id)
        })
      } else {
        this.records_forecast.forEach((e) => {
          this.selected_records.delete(e.record.id)
          this.selected_full_records.delete(e.record)
          this.selected_records_forecast.delete(e.id)
        })
      }
    }
  },

  created() {
    setTimeout(() => {
      this.selected_group = {
        id: null,
        name: this.$t("All Accounts"),
        value: null,
        type: this.current_object?.type
      }
    }, 500)
    this.forecast_obj = this.$store.state.data.user_forecasts.find((f) => f.id == this.forecast_name)
    if (this.forecast_obj) {
      this.refreshTableView()
      this.setForecastObjectType()
      this.getCurrentForecastCompanies()
    } else {
      dataService.getForecast(this.forecast_name).then((res) => {
        this.forecast_obj = res.data
        this.refreshTableView()
        this.setForecastObjectType()
        this.getCurrentForecastCompanies()
      })
    }

    this.register_listeners()

    const metrics_obj = {
      page_reference: PAGE_REFERENCES.FORECASTING,
      object_type: this.forecast_obj?.type,
      view_type: "table"
    }
    if (this.$route.params?.page) {
      metrics_obj["layout_page_reference"] = this.$route.params?.page
    }
    if (this.componentReference) {
      metrics_obj["component_reference"] = this.componentReference
    }
    this.displayed_metrics = this.getViewMetrics(metrics_obj)
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.first_loading = false
    this.unregister_listeners()
  },
  unmounted() {
    this.first_loading = false
    this.unregister_listeners()
  },
  methods: {
    updatePage({ page, page_size }) {
      this.getCurrentForecastCompanies(null, page, page_size)
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.UPDATE_FORECASTING, (event_data) => {
        if (event_data) {
          if (this.forecast_obj?.forecasting_settings == event_data.forecasting_settings) {
            this.setForecastObjectType()
            this.getCurrentForecastCompanies()
            this.refreshRollups()
          }
        }
      })
      this.$emitter.on(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_DASHBOARD_FORECASTING_TABLE, ({ id: id, count: count }) => {
        let index = this.records_forecast.findIndex((record_forecast) => record_forecast.id == id)
        this.records_forecast[index].notes_summary.count = count
      })
      this.$emitter.on(ALL_EMITTERS.REFRESH_FORECAST, () => {
        this.getForecasts()
      })
      this.$emitter.on(ALL_EMITTERS.DELETE_FORECAST, () => {
        this.getForecasts()
      })

      this.$emitter.on(ALL_EMITTERS.UPDATE_RECORD_IN_FORECASTING_TABLE, ({ new_record, component_ref }) => {
        if (component_ref !== this.componentReference) {
          const index = this.forecast_records.findIndex((f) => f.id == new_record.id)
          if (index !== -1) {
            this.forecast_records.splice(index, 1, new_record)
          }
        }
      })
      this.$emitter.on(ALL_EMITTERS.HIDE_COMPANIES_FROM_FORECAST, () => {
        this.hide_company_modal_is_open = true
      })
      this.$emitter.on(ALL_EMITTERS.SHOW_HIDDEN_COMPANIES_FROM_FORECAST, () => {
        this.getAllHiddenCompaniesForecast()
        this.show_hidden_company_modal_is_open = true
      })
      this.$emitter.on(ALL_EMITTERS.DELETE_COMPANIES_FROM_FORECAST, () => {
        this.remove_company_modal_is_open = true
      })
      if (!this.first_loading) {
        this.first_loading = true
        this.$emitter.on(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, (companies_ref) => {
          if (this.componentReference == companies_ref) {
            this.exportAsCSV("forecasting_table")
            this.$toast.open({
              message: `${this.selected_records.size} of ${
                this.$store.state.object_types.find((o) => o.id == this.objectType)?.type
              } table has been exported`,
              position: "top",
              type: "success",
              duration: 3000
            })
          }
        })
      }
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.UPDATE_FORECASTING)
      this.$emitter.off(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_DASHBOARD_FORECASTING_TABLE)
      this.$emitter.off(ALL_EMITTERS.REFRESH_FORECAST)
      this.$emitter.off(ALL_EMITTERS.DELETE_FORECAST)
      this.$emitter.off(ALL_EMITTERS.UPDATE_RECORD_IN_FORECASTING_TABLE)
      this.$emitter.off(ALL_EMITTERS.HIDE_COMPANIES_FROM_FORECAST)
      this.$emitter.off(ALL_EMITTERS.DELETE_COMPANIES_FROM_FORECAST)
      this.$emitter.off(ALL_EMITTERS.EXPORT_TABLE_TO_CSV)
    },
    showAllAssignedTags(record_forecast) {
      if (record_forecast.record?.is_all_assigned_tags_shown) {
        record_forecast.record["is_all_assigned_tags_shown"] = false
      } else {
        record_forecast.record["is_all_assigned_tags_shown"] = true
      }
    },
    toggleRecordFromSelectedList(record_forecast, checked) {
      if (checked) {
        this.selected_records.add(record_forecast.record.id)
        this.selected_full_records.add(record_forecast.record)
        this.selected_records_forecast.add(record_forecast.id)
      } else {
        this.selected_records.delete(record_forecast.record.id)
        this.selected_full_records.delete(record_forecast.record)
        this.selected_records_forecast.delete(record_forecast.id)
      }
    },
    async getForecasts() {
      dataService.getForecasts({ page_size: 100 }).then((result) => {
        if (result.data) {
          if (result.data?.results.length > 0) {
            this.user_forecasts = result.data.results
            this.forecasted_objects = utils.uniqBy(this.user_forecasts, "type").map((x) => {
              return { ...utils.getObjectTypeById(x.object_type) }
            })
            if (this.user_forecasts.length > 0) {
              this.current_object = this.forecasted_objects[0]
            } else {
              this.current_object = null
            }
          } else {
            this.current_object = null
            this.current_forecast = null
            this.user_forecasts = []
            this.forecasted_objects = []
          }
        }
      })
    },
    // refreshTableView() {
    //   if (Array.isArray(this.$store.state.ui_options?.forecast_table_headers)) {
    //     this.headers = this.$store.state.ui_options.forecast_table_headers.map((header_id) => {
    //       let header = this.all_headers.find((x) => x.id == header_id) || { id: header_id, label: header_id }
    //       return { ...header }
    //     })
    //   } else {
    //     this.headers = this.all_headers.map((header) => header)
    //     this.$store.dispatch("ui/setUIValue", {
    //       name: "forecast_table_headers",
    //       value: this.all_headers.map((x) => x.id)
    //     })
    //   }
    // },

    refreshTableView() {
      if (this.$store.state.ui_options.forecast_table_headers) {
        if (this.$store.state.ui_options.forecast_table_headers[this.componentReference]) {
          this.headers = this.$store.state.ui_options.forecast_table_headers[this.componentReference].map((header_id) => {
            let header = this.all_headers.find((x) => x.id == header_id) || {
              id: header_id,
              label: header_id
            }
            return { ...header }
          })
        } else {
          this.headers = this.all_headers.map((header) => header)
          this.$store.dispatch("ui/setUIValue", {
            forecast_table_headers: {
              ...this.$store.state.ui_options.forecast_table_headers,
              [this.componentReference]: this.all_headers.map((x) => x.id)
            }
          })
        }
      } else {
        this.headers = this.all_headers.map((header) => header)
        this.$store.dispatch("ui/setUIValue", {
          forecast_table_headers: {
            ...this.$store.state.ui_options.forecast_table_headers,
            [this.componentReference]: this.all_headers.map((x) => x.id)
          }
        })
      }
      this.headers.unshift({ label: "", id: "checkbox" })
    },
    updateForecastHeaders() {},
    filterBaseValue() {
      this.is_input_base_value = false
      if (this.base_value_gte || this.base_value_lte) {
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedBaseValue",
          data: {
            base_value_gte: this.base_value_gte,
            base_value_lte: this.base_value_lte
          }
        })
      }
    },
    cancelFilterBaseValue() {
      this.is_input_base_value = false
      if (this.base_value_gte || this.base_value_lte) {
        this.base_value_gte = null
        this.base_value_lte = null
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedBaseValue",
          data: {
            base_value_gte: this.base_value_gte,
            base_value_lte: this.base_value_lte
          }
        })
      }
    },
    filterForecastedValue() {
      this.is_input_forecasted_value = false
      if (this.forecasted_value_gte || this.forecasted_value_lte) {
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValue",
          data: {
            forecasted_value_gte: this.forecasted_value_gte,
            forecasted_value_lte: this.forecasted_value_lte
          }
        })
      }
    },
    cancelFilterForecastedValue() {
      this.is_input_forecasted_value = false
      if (this.forecasted_value_gte || this.forecasted_value_lte) {
        this.forecasted_value_gte = null
        this.forecasted_value_lte = null
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValue",
          data: {
            forecasted_value_gte: this.forecasted_value_gte,
            forecasted_value_lte: this.forecasted_value_lte
          }
        })
      }
    },
    filterForecastedValueMin() {
      this.is_input_forecasted_value_min = false
      if (this.forecasted_value_min_gte || this.forecasted_value_min_lte) {
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValueMin",
          data: {
            forecasted_value_min_gte: this.forecasted_value_min_gte,
            forecasted_value_min_lte: this.forecasted_value_min_lte
          }
        })
      }
    },
    cancelFilterForecastedValueMin() {
      this.is_input_forecasted_value_min = false
      if (this.forecasted_value_min_gte || this.forecasted_value_min_lte) {
        this.forecasted_value_min_gte = null
        this.forecasted_value_min_lte = null
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValueMin",
          data: {
            forecasted_value_min_gte: this.forecasted_value_min_gte,
            forecasted_value_min_lte: this.forecasted_value_min_lte
          }
        })
        this.getCurrentForecastCompanies()
      }
    },
    filterForecastedValueMax() {
      this.is_input_forecasted_value_max = false
      if (this.forecasted_value_max_gte || this.forecasted_value_max_lte) {
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValueMax",
          data: {
            forecasted_value_max_gte: this.forecasted_value_max_gte,
            forecasted_value_max_lte: this.forecasted_value_max_lte
          }
        })
      }
    },
    cancelFilterForecastedValueMax() {
      this.is_input_forecasted_value_max = false
      if (this.forecasted_value_max_gte || this.forecasted_value_max_lte) {
        this.forecasted_value_max_gte = null
        this.forecasted_value_max_lte = null
        this.getCurrentForecastCompanies()
        this.$emitter.emit(this.componentReference, {
          event: "forecastedValueMax",
          data: {
            forecasted_value_max_gte: this.forecasted_value_max_gte,
            forecasted_value_max_lte: this.forecasted_value_max_lte
          }
        })
      }
    },

    handleClickFilter() {
      this.is_filter_open = !this.is_filter_open
      if (!this.is_filter_open) {
        this.resetFilters()
      }
    },
    resetFilters() {
      if (
        this.search_term ||
        this.filter_selected_co_owners?.length ||
        this.filter_selected_owners?.length ||
        this.filter_selected_tags?.length ||
        this.forecast_status ||
        this.forecast_type ||
        this.base_value_gte ||
        this.base_value_lte ||
        this.forecasted_value_gte ||
        this.forecasted_value_lte ||
        this.forecast_date_range
      ) {
        this.base_value_gte = null
        this.base_value_lte = null
        this.forecasted_value_gte = null
        this.forecasted_value_lte = null
        this.search_term = ""
        this.filter_selected_co_owners = []
        this.filter_selected_owners = []
        this.filter_selected_tags = []
        this.forecast_status = null
        this.forecast_type = null
        this.forecast_date_range = null
        this.is_input_base_value = false
        this.is_input_forecasted_value = false
        this.refreshRollups()
      }
    },
    refreshRollups(trigger = null) {
      this.$emitter.emit(this.componentReference, {
        event: "recordsChange",
        data: this.filters,
        trigger
      })
    },

    setForecastObjectType() {
      this.forecast_object_type_id = this.forecast_obj?.object_type
      this.forecast_object_type = this.forecast_obj?.type
    },

    // separateWithValueStats(stats) {
    //   // we try to separate the ones with value from the ones without value because we will need to display them differently based on the presence of the attribute
    //   // let withValueList = []
    //   // let withoutValueList = []
    //   // stats.map((stat) => {
    //   //   if (Object.hasOwn(stat, "value")) {
    //   //     if (stat.value == null) {
    //   //       stat.value = 0
    //   //     }
    //   //     withValueList.push(stat)
    //   //   } else {
    //   //     withoutValueList.push(stat)
    //   //   }
    //   // })
    //   // this.forecast_stats_with_value = withValueList
    //   // this.forecast_stats_without_value = withoutValueList
    // },

    confirmCompanyForecastColor(company, color) {
      if (parseFloat(company.forecasted_value) >= parseFloat(company.renewal_base_value) && company.forecast_status == "actual") {
        return color == "green"
      } else if (parseFloat(company.forecasted_value) < parseFloat(company.renewal_base_value) && company.forecast_status == "actual") {
        return color == "red"
      } else if (
        parseFloat(company.forecasted_value) >= parseFloat(company.renewal_base_value) &&
        company.forecast_status == "forecasted"
      ) {
        return color == "yellow"
      } else if (parseFloat(company.forecasted_value) < parseFloat(company.renewal_base_value) && company.forecast_status == "forecasted") {
        return color == "orange"
      } else {
        return color == "gray"
      }
    },
    updateRecordForecast(forecast, trigger = null) {
      let data = {
        id: forecast.id,
        forecast_status: forecast.forecast_status,
        forecast_type: forecast.forecast_type,
        renewal_base_value: forecast.renewal_base_value,
        forecasted_value: forecast.forecasted_value,
        forecasted_value_max: forecast.forecasted_value_max,
        forecasted_value_min: forecast.forecasted_value_min,
        forecasted_date: this.$moment(forecast.forecasted_date).toISOString()
      }
      this.updating_forecast = trigger
      dataService.updateRecordForecast(this.forecast_obj?.id, forecast.id, data).then((result) => {
        let index = this.records_forecast.findIndex((c) => c.id == result.data.id)
        let record = { record: forecast.record }
        let new_data = { ...forecast, ...result.data, ...record }

        // trigger the rollup update
        this.refreshRollups(trigger)
        this.updating_forecast = null
        if (
          trigger == "forecasted_date" &&
          this.$moment(forecast?.forecasted_date).quarter() != this.$moment(this.selected_range.start).quarter()
        ) {
          this.$toast.open({
            message: `${Record.from(forecast.record).name} has been moved to <strong>Q${this.$moment(
              forecast?.forecasted_date
            ).quarter()}</strong> ${this.$moment(forecast?.forecasted_date).format("YYYY")}`,
            position: "top",
            type: "success",
            duration: 3000
          })
          this.records_forecast.splice(index, 1)
        } else {
          this.records_forecast.splice(index, 1, new_data)
        }
      })
    },
    removeCompaniesFromForecast() {
      let count = 0
      Array.from(this.selected_records_forecast).map((id) => {
        dataService.removeCompanyForecast(this.forecast_obj?.id, id).then(() => {
          this.records_forecast_pagination["count"] = this.records_forecast_pagination["count"] - 1
          count = count + 1
          let index = this.records_forecast.findIndex((c) => c.id == id)
          this.records_forecast.splice(index, 1)
          if (count == Array.from(this.selected_records_forecast).length) {
            this.remove_company_modal_is_open = false
            this.$toast.open({
              message: `Records removed to the forecast list`,
              position: "top",
              type: "success",
              duration: 3000
            })
            this.refreshRollups()
          }
        })
      })
    },
    getAllHiddenCompaniesForecast() {
      dataService.getAllHiddenCompaniesForecast(this.forecast_obj?.id).then((result) => {
        this.selected_hidden_records_forecast = []
        this.hidden_records_forecast = result.data
      })
    },
    handleSelectHiddenRecord(e, record) {
      if (e.target.checked) {
        this.selected_hidden_records_forecast.push(record.id)
      } else {
        const index = this.selected_hidden_records_forecast.findIndex((r) => r === record.id)
        this.selected_hidden_records_forecast.splice(index, 1)
      }
    },
    ShowHiddenCompaniesFromForecast() {
      let count = 0
      let data = {}

      this.selected_hidden_records_forecast.map((id) => {
        let rec = this.hidden_records_forecast.find((r) => r.id == id)
        data = { users: rec.hidden_from }
        dataService.showHiddenCompanyForecast(this.forecast_obj?.id, id, data).then(() => {
          count = count + 1
          if (count == this.selected_hidden_records_forecast.length) {
            this.show_hidden_company_modal_is_open = false
            let msg = `${count} ${count == 1 ? "record" : "records"} has been shown from the forecast list`
            this.$toast.open({
              message: msg,
              position: "top",
              type: "success",
              duration: 3000
            })
            this.selected_records = new Set()
            this.getCurrentForecastCompanies()
          }
        })
      })
    },
    hideCompaniesFromForecast() {
      let count = 0
      let data = { users: this.selected_users.map((x) => x.id) }
      Array.from(this.selected_records_forecast).map((id) => {
        dataService.hideCompanyForecast(this.forecast_obj?.id, id, data).then(() => {
          count = count + 1
          let index = this.records_forecast.findIndex((c) => c.id == id)
          this.records_forecast.splice(index, 1)
          if (count == Array.from(this.selected_records_forecast).length) {
            this.hide_company_modal_is_open = false
            let msg = `Records has been hidden from the forecast list of ${this.selected_users.length} team members`
            if (this.selected_users.map((x) => x.id).includes(this.$store.state.auth.user.id) && this.selected_users.length == 1) {
              msg = `Records has been hidden from your forecast list`
            } else if (!this.selected_users.map((x) => x.id).includes(this.$store.state.auth.user.id) && this.selected_users.length == 1) {
              msg = `Records has been hidden from ${this.selected_users[0].full_name}'s forecast list`
            }
            this.$toast.open({
              message: msg,
              position: "top",
              type: "success",
              duration: 3000
            })
            this.selected_users = [this.$store.state.auth.user]
            this.refreshRollups()
          }
        })
      })
    },
    getCurrentForecastCompanies(update_forecast_ranges = false, page = 1, page_size = 10) {
      if (this.view_by) {
        this.filters["group"] = this.view_by
      }
      this.is_loading = true
      if (this.forecast_obj) {
        this.$store.dispatch("ui/showLoading")
        dataService
          .findForecastCompaniesByFilters(this.forecast_obj.id, {
            ...this.filters,
            page,
            page_size
          })
          .then((result) => {
            this.is_loading = false
            this.records_forecast = result.data.results.map((r) => {
              r.record["checked"] = this.selected_records.has(r.record.id)
              return r
            })
            this.records_forecast_pagination = result.data
            this.$store.dispatch("ui/hideLoading")
            // this.refreshRollups()
            if (update_forecast_ranges) {
              this.$emitter.emit(ALL_EMITTERS.UPDATE_FORECAST, this.forecast_obj)
            }
            this.$emitter.emit(this.componentReference, {
              event: "selectedGroup",
              data: this.view_by ? this.view_by : this.selected_group?.id ? this.selected_group.id : null
            })
          })
          .catch(() => {
            this.is_loading = false
            this.$store.dispatch("ui/hideLoading")
          })
      }
    },
    exportAsCSV(table_id, separator = ",") {
      // Select rows from table_id
      var rows = document.getElementById(table_id).querySelectorAll("tr")
      // Construct csv
      var csv = []
      for (var i = 0; i < rows.length; i++) {
        var row = [],
          cols = rows[i].querySelectorAll("td, th")
        if (!rows[i].getAttribute("id") || Array.from(this.selected_records).includes(rows[i].getAttribute("id"))) {
          for (var j = 0; j < cols.length; j++) {
            var data = null
            if (cols[j]?.getElementsByTagName("select")) {
              data = cols[j]
                ?.getElementsByTagName("select")[0]
                ?.value?.replace(/(\r\n|\n|\r)/gm, "")
                .replace(/(\s\s)/gm, " ")
            }
            if (!data && cols[j]?.getElementsByTagName("input") && cols[j]?.getElementsByTagName("input")[0]?.type != "checkbox") {
              data = cols[j]
                ?.getElementsByTagName("input")[0]
                ?.value?.replace(/(\r\n|\n|\r)/gm, "")
                .replace(/(\s\s)/gm, " ")
            }
            if (!data) {
              data = cols[j]?.innerText?.replace(/(\r\n|\n|\r)/gm, "").replace(/(\s\s)/gm, " ")
            }
            // Escape double-quote with double-double-quote (see https://stackoverflow.com/questions/17808511/properly-escape-a-double-quote-in-csv)
            if (data) {
              data = data.replace(/"/g, '""')
            } else {
              data = ""
            }
            // Push escaped string
            row.push('"' + data + '"')
          }
          csv.push(row.join(separator))
        }
      }
      var csv_string = csv.join("\n")
      // Download it
      var filename = "export_" + table_id + "_" + new Date().toLocaleDateString() + ".csv"
      var link = document.createElement("a")
      link.style.display = "none"
      link.setAttribute("target", "_blank")
      link.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(csv_string))
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    // this is unused rn as the adding of the company will be done off the add company modal which is a drop down now
    // TODO: maybe remove this at some point when it's redundant for sure
    addCompany(company) {
      //just a comment
      let forecasted_date = this.selected_range.start
      if (this.$moment().quarter() == forecasted_date.quarter()) {
        forecasted_date = this.$moment()
      }
      let data = {
        record: company.id,
        object_type: this.forecast_object_type_id,
        forecasted_date: this.$moment(forecasted_date).toISOString()
      }
      dataService.addCompanyForecast(this.forecast_obj?.id, data).then((res) => {
        this.records_forecast_pagination["count"] = this.records_forecast_pagination["count"] + 1
        this.$toast.open({
          message: `<strong>${Record.from(company).name}</strong> added to the forecast list`,
          position: "top",
          type: "success",
          duration: 3000
        })

        this.records_forecast.unshift(res.data)
        this.add_company_modal_is_open = false
      })
    },
    openModalNotes(company) {
      this.$emitter.emit(ALL_EMITTERS.ADD_NOTES_FORECAST, {
        forecast_id: this.forecast_obj?.id,
        company: company
      })
    }
  }
}
</script>
